import {
  BarChart3,
  Briefcase,
  Building2,
  CalendarCheck,
  CheckSquare,
  ClipboardCheck,
  FileSignature,
  LayoutDashboard,
  Network,
  Settings,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { createContext, useContext, type ReactNode } from 'react';
import type { Tone } from './Tone';

// The part of the app a screen belongs to, which gives every plain card on it a tone and an
// icon tile (ADR 0035; DECISIONS-LOG 2026-10-06 "Tone style app-wide" and "A colour for every
// status"). Every area has its own colour; none is grey. The icons are the module bar's, so a
// card's tile is the icon that was clicked to get there.

export type AreaKey =
  | 'dashboard'
  | 'organisation'
  | 'requisitions'
  | 'jobs'
  | 'candidates'
  | 'assessments'
  | 'interviews'
  | 'offers'
  | 'tasks'
  | 'reports'
  | 'admin';

export interface ToneArea {
  readonly tone: Tone;
  readonly icon: LucideIcon;
}

export const TONE_AREAS: Readonly<Record<AreaKey, ToneArea>> = {
  dashboard: { tone: 'dashboard', icon: LayoutDashboard },
  organisation: { tone: 'organisation', icon: Network },
  requisitions: { tone: 'requisition', icon: Building2 },
  jobs: { tone: 'job', icon: Briefcase },
  candidates: { tone: 'screening', icon: Users },
  assessments: { tone: 'assessment', icon: ClipboardCheck },
  interviews: { tone: 'interview', icon: CalendarCheck },
  offers: { tone: 'offer', icon: FileSignature },
  tasks: { tone: 'tasks', icon: CheckSquare },
  reports: { tone: 'reports', icon: BarChart3 },
  admin: { tone: 'admin', icon: Settings },
};

// A plain prefix match: `/interview` also covers `/interviews`, `/interview-kits` and
// `/interview-plans`, and `/offer` covers `/offers` and `/offer-letters`.
const AREA_PREFIXES: readonly (readonly [string, AreaKey])[] = [
  ['/workspace', 'dashboard'],
  ['/notifications', 'dashboard'],
  ['/search', 'dashboard'],
  ['/organisation', 'organisation'],
  ['/requisitions', 'requisitions'],
  ['/request-hire', 'requisitions'],
  ['/approval', 'requisitions'],
  ['/my/', 'requisitions'],
  ['/jobs', 'jobs'],
  ['/pipelines', 'jobs'],
  ['/role-templates', 'jobs'],
  ['/candidates', 'candidates'],
  ['/applications', 'candidates'],
  ['/pools', 'candidates'],
  ['/cv-bank', 'candidates'],
  ['/referrals', 'candidates'],
  ['/assessments', 'assessments'],
  ['/test', 'assessments'],
  ['/interview', 'interviews'],
  ['/rubrics', 'interviews'],
  ['/rooms', 'interviews'],
  ['/calendars', 'interviews'],
  ['/offer', 'offers'],
  ['/tasks', 'tasks'],
  ['/reports', 'reports'],
];

/** Anything not named above is configuration, so it sits in Admin. */
export function areaOf(pathname: string): AreaKey {
  if (pathname === '/') return 'dashboard';
  const match = AREA_PREFIXES.find(([prefix]) => pathname.startsWith(prefix));
  return match ? match[1] : 'admin';
}

const ToneAreaContext = createContext<ToneArea>(TONE_AREAS.dashboard);

/**
 * Wraps the content region only, so a navigation re-renders what is already changing and
 * never the shell (CLAUDE.md §5a rule 6). It updates only when the area changes.
 */
export function ToneAreaProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const area = useRouterState({ select: (state) => areaOf(state.location.pathname) });
  return <ToneAreaContext.Provider value={TONE_AREAS[area]}>{children}</ToneAreaContext.Provider>;
}

export function useToneArea(): ToneArea {
  return useContext(ToneAreaContext);
}
